import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Text, Box } from 'components/ui';
import { colors } from 'style/config';
import anaUtils from 'analytics/utils';
import { sendSOTLinkTrackingEvent } from 'analytics/utils/sotTracking';
import { socialProofSelector } from 'selectors/page/product/productViews/productViewsSelector';
import { showSessionAIMsgSelector } from 'viewModel/selectors/testTarget/showSessionAIMsgSelector';
import { showSocialProofPDPSelector } from 'viewModel/selectors/testTarget/showSocialProofPDPSelector';

const renderBanner = (content, {
    showSocialProofPDP, blackBackground, extraMargin, unlockWidth, transparentStyle
}) => {
    const isChallenger = showSocialProofPDP.challengerOne || showSocialProofPDP.challengerTwo;

    const isTransparentStyle = (isChallenger && !blackBackground) || transparentStyle;

    return (
        <Box
            role='status'
            aria-live='polite'
            aria-label={content}
            marginTop={4}
            backgroundColor={colors.black}
            borderRadius={2}
            paddingLeft={2}
            paddingRight={2}
            width='fit-content'
            {...(!unlockWidth && { maxWidth: '375px' })}
            {...(isTransparentStyle && { marginTop: transparentStyle ? 0 : extraMargin ? '2px' : 0, paddingLeft: 0, backgroundColor: 'unset' })}
        >
            <Text
                is='p'
                lineHeight='24px'
                fontSize='sm'
                color={colors.white}
                {...(isTransparentStyle && { color: colors.black, fontWeight: 'bold', lineHeight: 1, verticalAlign: 'middle' })}
            >
                {content}
            </Text>
        </Box>
    );
};

const ProductViewsCount = ({ blackBackground, extraMargin, unlockWidth = false, transparentStyle = false }) => {
    const socialProof = useSelector(socialProofSelector);
    const showSessionAIMsg = useSelector(showSessionAIMsgSelector);
    const showSocialProofPDP = useSelector(showSocialProofPDPSelector);
    const lastFiredProductIdRef = useRef(null);

    const bannerProps = {
        showSocialProofPDP,
        blackBackground,
        extraMargin,
        unlockWidth,
        transparentStyle
    };

    const display = socialProof?.display;
    const message = socialProof?.message;
    const productId = socialProof?.productId;

    const hasActiveExperience =
        showSessionAIMsg || showSocialProofPDP?.control || showSocialProofPDP?.challengerOne || showSocialProofPDP?.challengerTwo;

    const shouldDisplay = !!(display && message && hasActiveExperience);

    useEffect(() => {
        if (!shouldDisplay || lastFiredProductIdRef.current === productId) {
            return;
        }

        lastFiredProductIdRef.current = productId;

        anaUtils.setNextPageData({
            linkData: `in-session:social proofing message:${socialProof.lookbackWindow ?? '24h'}`
        });

        try {
            sendSOTLinkTrackingEvent({
                eventData: {
                    linkName: 'social proof display',
                    actionInfo: 'social proof display',
                    specificEventName: 'social proof display',
                    sotType: 'social proof display',
                    customPayload: {
                        'pdp.socialProof.dataPoint': String(socialProof.dataPoint ?? ''),
                        'pdp.socialProof.count': String(socialProof.count ?? ''),
                        'pdp.socialProof.threshold': String(socialProof.threshold ?? ''),
                        'pdp.socialProof.lookbackHours': String(socialProof.lookbackHours ?? ''),
                        'pdp.socialProof.market': String(socialProof.market ?? ''),
                        'pdp.socialProof.productId': String(productId ?? '')
                    }
                }
            });
        } catch (e) {
            // fail-silent
        }
    }, [shouldDisplay, productId, socialProof]);

    if (!shouldDisplay) {
        return null;
    }

    return renderBanner(message, bannerProps);
};

export default wrapFunctionalComponent(ProductViewsCount, 'ProductViewsCount');
